import { useNavigate, useSearch } from '@tanstack/react-router';
import { Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { ActivityAdmin } from '@tt/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/misc';
import { NativeSelect } from '@/components/ui/native-select';
import { Segmented } from '@/components/ui/segmented';
import {
  useActivityMutations,
  useAdminActivities,
  useAdminGroups,
  useAdminProjects,
} from '@/lib/admin-queries';
import { describeError, fieldErrorsOf } from '@/lib/errors';
import { AdminPage, ConfirmDialog, DataTable, MultiSelect, Pagination } from './admin-ui';

type StatusFilter = 'ALL' | 'ACTIVE' | 'ARCHIVED';

/** The project filter's value for "belongs to no project"; not a project id. */
const UNASSIGNED = 'unassigned';

/** Activities: the shared catalog plus everything people created for themselves. */
export function AdminActivitiesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ from: '/app/admin/activities' });
  const [editing, setEditing] = useState<ActivityAdmin | 'new' | null>(null);
  const [deleting, setDeleting] = useState<ActivityAdmin | null>(null);
  const [promoting, setPromoting] = useState<ActivityAdmin | null>(null);
  const [merging, setMerging] = useState<ActivityAdmin | null>(null);

  const page = search.page ?? 1;
  const status: StatusFilter = (search.status as StatusFilter) ?? 'ACTIVE';
  // `unassigned` is a project filter in the UI but a separate flag on the API, because "no
  // project" is not a project id. Kept in one search param so the filter reads as one control.
  const unassigned = search.project === UNASSIGNED;
  const activities = useAdminActivities({
    q: search.q || undefined,
    type: search.type,
    projectId: unassigned ? undefined : search.project || undefined,
    unassigned,
    status: status === 'ALL' ? undefined : status,
    page,
    pageSize: 25,
  });
  const groups = useAdminGroups({ pageSize: 200 });
  // An activity belongs to a project, so creating one needs the list to choose from. Active only:
  // an archived project takes no new activities.
  const projects = useAdminProjects({ pageSize: 200, status: 'ACTIVE' });
  const mutations = useActivityMutations();

  const setSearch = (next: Record<string, unknown>) =>
    void navigate({
      to: '/admin/activities',
      search: { ...search, page: 1, ...next },
      replace: true,
    });

  const run = (promise: Promise<unknown>, message: string) =>
    promise.then(
      () => toast.success(message),
      (error: unknown) => toast.error(describeError(error, t)),
    );

  const rows = activities.data?.data ?? [];
  const groupOptions = (groups.data?.data ?? []).map((g) => ({ id: g.id, label: g.name }));
  const projectOptions = (projects.data?.data ?? []).map((p) => ({ id: p.id, label: p.name }));

  return (
    <AdminPage
      title={t('admin.activities.title')}
      description={t('admin.activities.subtitle')}
      actions={
        <Button onClick={() => setEditing('new')}>
          <Plus className="h-4 w-4" aria-hidden="true" /> {t('admin.activities.new')}
        </Button>
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="search"
          defaultValue={search.q ?? ''}
          onChange={(e) => setSearch({ q: e.target.value || undefined })}
          placeholder={t('admin.activities.search')}
          aria-label={t('admin.activities.search')}
          className="max-w-xs"
        />
        <NativeSelect
          aria-label={t('admin.activities.type')}
          value={search.type ?? ''}
          onChange={(e) => setSearch({ type: e.target.value || undefined })}
          className="w-40"
        >
          <option value="">{t('admin.all')}</option>
          <option value="SHARED">{t('admin.activities.shared')}</option>
          <option value="PERSONAL">{t('admin.activities.personal')}</option>
        </NativeSelect>
        <NativeSelect
          aria-label={t('admin.activities.project')}
          value={search.project ?? ''}
          onChange={(e) => setSearch({ project: e.target.value || undefined })}
          className="w-52"
        >
          <option value="">{t('admin.activities.allProjects')}</option>
          {projectOptions.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
          <option value={UNASSIGNED}>{t('admin.activities.unassigned')}</option>
        </NativeSelect>
        <Segmented
          value={status}
          onChange={(v) => setSearch({ status: v })}
          aria-label={t('admin.projects.status')}
          options={[
            { value: 'ACTIVE', label: t('status.ACTIVE') },
            { value: 'ARCHIVED', label: t('status.ARCHIVED') },
            { value: 'ALL', label: t('admin.all') },
          ]}
        />
      </div>

      <DataTable
        caption={t('admin.activities.title')}
        isPending={activities.isPending}
        error={activities.isError ? describeError(activities.error, t) : undefined}
        empty={t('admin.activities.none')}
        rows={rows}
        rowKey={(a) => a.id}
        footer={
          <Pagination
            page={page}
            totalPages={activities.data?.meta.totalPages ?? 1}
            total={activities.data?.meta.total ?? 0}
            onChange={(next) =>
              void navigate({ to: '/admin/activities', search: { ...search, page: next } })
            }
          />
        }
        columns={[
          {
            key: 'name',
            header: t('admin.activities.name'),
            cell: (a) => (
              <span className="inline-flex flex-wrap items-center gap-2">
                <span>
                  <span className="block font-medium">{a.name}</span>
                  {a.description ? (
                    <span className="block text-xs text-muted-foreground">{a.description}</span>
                  ) : null}
                </span>
                {a.type === 'PERSONAL' ? <Badge>{t('admin.activities.personal')}</Badge> : null}
                {a.status === 'ARCHIVED' ? <Badge>{t('status.ARCHIVED')}</Badge> : null}
                {a.mergedIntoActivityId ? (
                  <span className="text-xs text-muted-foreground">
                    {t('admin.activities.mergedInto')}
                  </span>
                ) : null}
              </span>
            ),
          },
          {
            key: 'project',
            header: t('admin.activities.project'),
            cell: (a) =>
              a.project ? (
                a.project.name
              ) : (
                // Not a dash: an activity with no project can be booked to nowhere, which is
                // worth saying rather than leaving blank.
                <Badge>{t('admin.activities.unassignedShort')}</Badge>
              ),
          },
          {
            key: 'owner',
            header: t('admin.activities.owner'),
            cell: (a) => a.owner?.name ?? <span className="text-muted-foreground">—</span>,
          },
          {
            key: 'groups',
            header: t('admin.groups.title'),
            cell: (a) =>
              a.type === 'SHARED' ? (
                <GroupCell
                  label={t('admin.activities.assignGroups', { name: a.name })}
                  count={a.groupCount}
                  options={groupOptions}
                  onChange={(ids) =>
                    void run(
                      mutations.setGroups.mutateAsync({ id: a.id, ids }),
                      t('admin.activities.groupsSaved', { name: a.name }),
                    )
                  }
                />
              ) : (
                <span className="text-muted-foreground">—</span>
              ),
          },
          {
            key: 'entries',
            header: t('admin.activities.entries'),
            numeric: true,
            cell: (a) => a.entryCount,
          },
          {
            key: 'actions',
            header: <span className="sr-only">{t('admin.actions')}</span>,
            numeric: true,
            cell: (a) => (
              <div className="flex flex-wrap justify-end gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditing(a)}
                  aria-label={t('admin.activities.editName', { name: a.name })}
                >
                  <Pencil className="h-4 w-4" aria-hidden="true" />
                </Button>
                {a.type === 'PERSONAL' ? (
                  <Button variant="ghost" size="sm" onClick={() => setPromoting(a)}>
                    {t('admin.activities.promote')}
                  </Button>
                ) : null}
                <Button variant="ghost" size="sm" onClick={() => setMerging(a)}>
                  {t('admin.activities.merge')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    void run(
                      mutations.setArchived.mutateAsync({
                        id: a.id,
                        archived: a.status === 'ACTIVE',
                      }),
                      a.status === 'ACTIVE'
                        ? t('admin.activities.archived', { name: a.name })
                        : t('admin.activities.unarchived', { name: a.name }),
                    )
                  }
                >
                  {a.status === 'ACTIVE' ? t('admin.archive') : t('admin.unarchive')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={a.entryCount > 0}
                  title={a.entryCount > 0 ? t('admin.activities.deleteBlocked') : undefined}
                  onClick={() => setDeleting(a)}
                >
                  {t('app.delete')}
                </Button>
              </div>
            ),
          },
        ]}
      />

      {editing ? (
        <ActivityDialog
          activity={editing === 'new' ? null : editing}
          groupOptions={groupOptions}
          projectOptions={projectOptions}
          onClose={() => setEditing(null)}
        />
      ) : null}

      {promoting ? (
        <PromoteDialog
          activity={promoting}
          groupOptions={groupOptions}
          onClose={() => setPromoting(null)}
        />
      ) : null}

      {merging ? <MergeDialog activity={merging} onClose={() => setMerging(null)} /> : null}

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={t('admin.activities.deleteTitle', { name: deleting?.name ?? '' })}
        description={t('admin.activities.deleteBody')}
        confirmLabel={t('app.delete')}
        onConfirm={() => {
          if (!deleting) return;
          void run(
            mutations.remove.mutateAsync(deleting.id),
            t('admin.activities.deleted', { name: deleting.name }),
          );
        }}
      />
    </AdminPage>
  );
}

function GroupCell({
  label,
  count,
  options,
  onChange,
}: {
  label: string;
  count: number;
  options: { id: string; label: string }[];
  onChange: (ids: string[]) => void;
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string[] | null>(null);
  return (
    <MultiSelect
      label={label}
      options={options}
      selected={selected ?? []}
      emptyLabel={t('admin.projects.groupCount', { count })}
      onChange={(ids) => {
        setSelected(ids);
        onChange(ids);
      }}
    />
  );
}

function ActivityDialog({
  activity,
  groupOptions,
  projectOptions,
  onClose,
}: {
  activity: ActivityAdmin | null;
  groupOptions: { id: string; label: string }[];
  projectOptions: { id: string; label: string }[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const mutations = useActivityMutations();
  const [form, setForm] = useState({
    name: activity?.name ?? '',
    description: activity?.description ?? '',
    projectId: activity?.project?.id ?? '',
    groupIds: [] as string[],
  });
  const [error, setError] = useState<unknown>(null);
  const fieldErrors = fieldErrorsOf(error);

  const submit = async () => {
    setError(null);
    try {
      if (activity) {
        await mutations.update.mutateAsync({
          id: activity.id,
          name: form.name,
          description: form.description || null,
          // Sent only when it changed: the server refuses a move once anything has been booked
          // to the activity, so sending the current value every time would fail every save.
          ...(form.projectId && form.projectId !== (activity.project?.id ?? '')
            ? { projectId: form.projectId }
            : {}),
        });
        toast.success(t('admin.activities.saved', { name: form.name }));
      } else {
        await mutations.create.mutateAsync({
          projectId: form.projectId,
          name: form.name,
          description: form.description || null,
          groupIds: form.groupIds,
        });
        toast.success(t('admin.activities.created', { name: form.name }));
      }
      onClose();
    } catch (e) {
      setError(e);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {activity
              ? t('admin.activities.editName', { name: activity.name })
              : t('admin.activities.new')}
          </DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <Field label={t('admin.activities.name')} required error={fieldErrors.name}>
            {(props) => (
              <Input
                {...props}
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                required
              />
            )}
          </Field>
          <Field
            label={t('admin.activities.project')}
            hint={
              activity && activity.entryCount > 0
                ? t('admin.activities.projectLockedHelp', { count: activity.entryCount })
                : t('admin.activities.projectHelp')
            }
            error={fieldErrors.projectId}
            required
          >
            {(props) => (
              <NativeSelect
                {...props}
                value={form.projectId}
                onChange={(e) => setForm((f) => ({ ...f, projectId: e.target.value }))}
                disabled={!!activity && activity.entryCount > 0}
                required
              >
                <option value="">{t('admin.activities.selectProject')}</option>
                {projectOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Field label={t('admin.activities.description')} error={fieldErrors.description}>
            {(props) => (
              <Input
                {...props}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            )}
          </Field>
          {activity ? null : (
            <Field label={t('admin.groups.title')}>
              {() => (
                <MultiSelect
                  label={t('admin.groups.title')}
                  options={groupOptions}
                  selected={form.groupIds}
                  emptyLabel={t('admin.projects.noGroups')}
                  onChange={(groupIds) => setForm((f) => ({ ...f, groupIds }))}
                />
              )}
            </Field>
          )}
          {error && Object.keys(fieldErrors).length === 0 ? (
            <p className="text-sm text-destructive" role="alert">
              {describeError(error, t)}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('app.cancel')}
            </Button>
            <Button type="submit" disabled={!form.name.trim() || !form.projectId}>
              {t('app.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Personal → shared. The activity keeps its id, so nothing already reported moves. */
function PromoteDialog({
  activity,
  groupOptions,
  onClose,
}: {
  activity: ActivityAdmin;
  groupOptions: { id: string; label: string }[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const mutations = useActivityMutations();
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [error, setError] = useState<unknown>(null);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('admin.activities.promoteTitle', { name: activity.name })}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{t('admin.activities.promoteBody')}</p>
        <Field label={t('admin.groups.title')}>
          {() => (
            <MultiSelect
              label={t('admin.groups.title')}
              options={groupOptions}
              selected={groupIds}
              emptyLabel={t('admin.projects.noGroups')}
              onChange={setGroupIds}
            />
          )}
        </Field>
        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {describeError(error, t)}
          </p>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('app.cancel')}
          </Button>
          <Button
            onClick={() => {
              setError(null);
              mutations.promote
                .mutateAsync({ id: activity.id, groupIds })
                .then(() => {
                  toast.success(t('admin.activities.promoted', { name: activity.name }));
                  onClose();
                })
                .catch(setError);
            }}
          >
            {t('admin.activities.promote')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Merge a duplicate away: entries move, totals do not change. */
function MergeDialog({ activity, onClose }: { activity: ActivityAdmin; onClose: () => void }) {
  const { t } = useTranslation();
  const mutations = useActivityMutations();
  const candidates = useAdminActivities({ status: 'ACTIVE', type: 'SHARED', pageSize: 200 });
  const [target, setTarget] = useState('');
  const [error, setError] = useState<unknown>(null);

  const options = (candidates.data?.data ?? []).filter((a) => a.id !== activity.id);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('admin.activities.mergeTitle', { name: activity.name })}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {t('admin.activities.mergeBody', { name: activity.name })}
        </p>
        <Field label={t('admin.activities.mergeTarget')} required>
          {(props) => (
            <NativeSelect
              {...props}
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              className="w-full"
            >
              <option value="">—</option>
              {options.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {describeError(error, t)}
          </p>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('app.cancel')}
          </Button>
          <Button
            disabled={!target}
            onClick={() => {
              setError(null);
              mutations.merge
                .mutateAsync({ id: activity.id, targetActivityId: target })
                .then((result) => {
                  toast.success(
                    t('admin.activities.merged', {
                      count: result.movedEntries,
                      target: result.target.name,
                    }),
                  );
                  onClose();
                })
                .catch(setError);
            }}
          >
            {t('admin.activities.merge')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
